/**
 * Central Membership REST Client
 *
 * Calls Central membership endpoints DIRECTLY from the browser with Bearer tokens.
 * Same pattern as centralBilling.ts and centralNotifications.ts.
 *
 * Uses shared path builders and types from @levelup/central-auth.
 */

import {
  buildMembersPath,
  buildMemberPath,
  buildInvitesPath,
  buildInvitePath,
  buildInviteResendPath,
} from '@levelup/central-auth';
import type {
  CentralRole,
  WorkspaceMember,
  PendingInvite,
  InviteMemberRequest,
} from '@levelup/central-auth';
import { getAccessToken } from './centralAuth';

const RAW_CENTRAL_URL = import.meta.env.VITE_CENTRAL_URL;

function getCentralBase(): string | null {
  if (!RAW_CENTRAL_URL) return null;
  return RAW_CENTRAL_URL.replace(/\/+$/, '');
}

async function centralMembershipFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = getAccessToken();
  if (!token) throw new Error('Not authenticated');

  const base = getCentralBase();
  if (!base) throw new Error('VITE_CENTRAL_URL not configured');

  const url = new URL(path, base).toString();
  const response = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (response.status === 401 || response.status === 403) {
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    throw new Error(`Central returned ${response.status}`);
  }
  if (response.status === 204) return undefined as T;
  return response.json();
}

export async function listMembers(workspaceId: string): Promise<WorkspaceMember[]> {
  const res = await centralMembershipFetch<{ members: WorkspaceMember[] }>(
    buildMembersPath(workspaceId),
  );
  return res.members ?? [];
}

export async function listPendingInvites(workspaceId: string): Promise<PendingInvite[]> {
  const res = await centralMembershipFetch<{ invites: PendingInvite[] }>(
    buildInvitesPath(workspaceId),
  );
  return res.invites ?? [];
}

export async function inviteMember(
  workspaceId: string,
  params: InviteMemberRequest,
): Promise<PendingInvite> {
  const res = await centralMembershipFetch<{ invite: PendingInvite }>(
    buildInvitesPath(workspaceId),
    { method: 'POST', body: JSON.stringify(params) },
  );
  return res.invite;
}

export async function updateMemberRole(
  workspaceId: string,
  userId: string,
  newRole: CentralRole,
): Promise<void> {
  await centralMembershipFetch(buildMemberPath(workspaceId, userId), {
    method: 'PATCH',
    body: JSON.stringify({ userId, newRole }),
  });
}

export async function removeMember(workspaceId: string, userId: string): Promise<void> {
  await centralMembershipFetch(buildMemberPath(workspaceId, userId), {
    method: 'DELETE',
  });
}

export async function resendInvite(workspaceId: string, inviteId: string): Promise<void> {
  await centralMembershipFetch(buildInviteResendPath(workspaceId, inviteId), {
    method: 'POST',
  });
}

export async function cancelInvite(workspaceId: string, inviteId: string): Promise<void> {
  await centralMembershipFetch(buildInvitePath(workspaceId, inviteId), {
    method: 'DELETE',
  });
}
